/**
 * Shared worker conflict helpers for Planning modules.
 *
 * Schedule and Dispatch both treat non-skipped WorkScopeAspects as active bookings.
 * Dispatch also treats DayDetails.off_work as unavailable for assignment.
 */

import { toLocalDateKey } from "../../../utils/datetime/core";
import { getFiniteNumericIdSet } from "../../../utils/collections/numericId";
import { getWorkerDisplayLabel } from "../../../utils/business/worker";

/**
 * Find the first worker object with a numeric id across WorkScopeAspect assignments.
 *
 * @param {Array<Object>} aspects
 * @param {number} numericId
 * @returns {Object|undefined}
 */
function findWorkerObjectInAspectsByNumericId(aspects, numericId) {
  for (const aspect of aspects ?? []) {
    for (const worker of aspect.worker_assignment ?? []) {
      if (Number(worker?.id) === numericId) {
        return worker;
      }
    }
  }
  return undefined;
}

/**
 * Compare two worker assignment lists by numeric id, ignoring list order.
 *
 * @param {Array<Object>|null|undefined} leftAssignment
 * @param {Array<Object>|null|undefined} rightAssignment
 * @returns {boolean}
 */
function workerAssignmentsDiffer(leftAssignment, rightAssignment) {
  const leftSignature = [...getFiniteNumericIdSet(leftAssignment)]
    .sort((a, b) => a - b)
    .join(",");
  const rightSignature = [...getFiniteNumericIdSet(rightAssignment)]
    .sort((a, b) => a - b)
    .join(",");
  return leftSignature !== rightSignature;
}

/**
 * Stable fallback key for one WorkScopeAspect-like row.
 *
 * @param {Object} aspect
 * @returns {string}
 */
function getDefaultAspectStableKey(aspect) {
  if (aspect?.id != null) {
    return String(aspect.id);
  }
  const dateKey = aspect?.date != null ? toLocalDateKey(aspect.date) : "nodate";
  return `aspect:${dateKey}:${JSON.stringify(aspect?.worker_assignment ?? [])}`;
}

/**
 * For each date key, find workers assigned on more than one non-skipped aspect that day.
 *
 * @param {Array<Object>} mergedAspects Full list after applying any optimistic changes.
 * @param {string[]} dateKeys Calendar keys to evaluate.
 * @param {{ workers?: Array<Object>|null, getAspectStableKey?: (aspect: Object) => string }} [options]
 * @returns {Array<{ dateKey: string, conflictNames: string[], conflictWorkerIds: number[] }>}
 */
export function getWorkerConflictSummariesByDate(
  mergedAspects,
  dateKeys,
  { workers, getAspectStableKey = getDefaultAspectStableKey } = {}
) {
  const results = [];

  for (const dateKey of dateKeys) {
    const aspectsOnDay = (mergedAspects ?? []).filter(
      (a) => toLocalDateKey(a.date) === dateKey && !a.skipped
    );

    /** @type {Map<number, Set<string>>} */
    const workerToAspectKeys = new Map();

    for (const aspect of aspectsOnDay) {
      const stableKey = getAspectStableKey(aspect);
      for (const wid of getFiniteNumericIdSet(aspect.worker_assignment)) {
        if (!workerToAspectKeys.has(wid)) {
          workerToAspectKeys.set(wid, new Set());
        }
        workerToAspectKeys.get(wid).add(stableKey);
      }
    }

    const conflictIds = [];
    for (const [wid, set] of workerToAspectKeys) {
      if (set.size > 1) {
        conflictIds.push(wid);
      }
    }
    conflictIds.sort((a, b) => a - b);

    const conflictNames = conflictIds.map((wid) => {
      const assignmentWorker = findWorkerObjectInAspectsByNumericId(
        aspectsOnDay,
        wid
      );
      const catalogWorker =
        workers?.find((worker) => Number(worker?.id) === wid) ?? null;
      const worker = assignmentWorker ?? catalogWorker;
      return worker ? getWorkerDisplayLabel(worker) : String(wid);
    });

    results.push({
      dateKey,
      conflictNames,
      conflictWorkerIds: [...conflictIds],
    });
  }

  return results;
}

/**
 * Ordered numeric worker ids after removing double-booked ids from the effective crew for
 * one day: per-day override when it differs from the block default, otherwise block default.
 *
 * @param {{ worker_assignment?: Array<Object>|null }} block
 * @param {{ worker_assignment?: Array<Object>|null }} aspect
 * @param {Array<number|string>} conflictWorkerIds Workers double-booked that calendar day.
 * @returns {number[]}
 */
export function buildFilteredWorkerIdsAfterConflictStrip({
  block,
  aspect,
  conflictWorkerIds,
}) {
  const remove = new Set(
    (conflictWorkerIds ?? [])
      .map((n) => Number(n))
      .filter((x) => Number.isFinite(x))
  );
  const hasOverride = workerAssignmentsDiffer(
    block?.worker_assignment,
    aspect?.worker_assignment
  );
  const sourceList = hasOverride
    ? (aspect?.worker_assignment ?? [])
    : (block?.worker_assignment ?? []);
  return getWorkerIdsExcluding(sourceList, remove);
}

/**
 * Numeric ids from a worker assignment list, preserving list order and excluding ids.
 *
 * @param {Array<Object>|null|undefined} workerAssignment
 * @param {Set<number>|Array<number>} excludedWorkerIds
 * @returns {Array<number>}
 */
export function getWorkerIdsExcluding(workerAssignment, excludedWorkerIds) {
  const remove =
    excludedWorkerIds instanceof Set
      ? excludedWorkerIds
      : new Set(
        (excludedWorkerIds ?? [])
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id))
      );
  const ordered = [];
  for (const worker of workerAssignment ?? []) {
    const id = Number(worker?.id);
    if (Number.isFinite(id) && !remove.has(id)) {
      ordered.push(id);
    }
  }
  return ordered;
}

/**
 * Worker ids that would be unavailable when unskipping one Dispatch scope:
 * active same-day scope assignments plus DayDetails.off_work, excluding the target scope.
 *
 * @param {Object} params
 * @param {Object} params.scope Target WorkScopeAspect being unskipped.
 * @param {Array<Object>} params.scopes Weekly scopes.
 * @param {Array<Object>} params.dayDetails Dispatch day-details rows.
 * @returns {Set<number>}
 */
export function getDispatchUnavailableWorkerIdsForUnskip({
  scope,
  scopes,
  dayDetails,
}) {
  const unavailable = new Set();
  const scopeId = String(scope.id);
  const scopeDate = scope.date;

  for (const row of scopes ?? []) {
    if (
      String(row.id) === scopeId ||
      row.date !== scopeDate ||
      row.skipped
    ) {
      continue;
    }
    for (const workerId of getFiniteNumericIdSet(row.worker_assignment)) {
      unavailable.add(workerId);
    }
  }

  const day = (dayDetails ?? []).find((entry) => entry.date === scopeDate);
  for (const workerId of getFiniteNumericIdSet(day?.off_work)) {
    unavailable.add(workerId);
  }

  return unavailable;
}
